import moment, {type Moment} from 'moment-timezone';

import {hasValue} from '@famly/stat_ts-utils_has-value';
import {type Option} from 'web-app/util/moment';

import * as Constants from './constants';

interface Period {
    from?: string;
    to?: string;
}

export interface InvoicingFormData {
    periodType?: string;
    invoiceMonth: string;
    invoiceWeek: string;
    from?: string;
    to?: string;
    due?: string;
}

export type FirstDayOfWeekValues = 'SUNDAY' | 'MONDAY';

export const createWeekValue = (start: Moment, end: Moment) => {
    return `${start.format('YYYY-MM-DD')}|${end.format('YYYY-MM-DD')}`;
};

export const firstDayOfWeekIsoValue = (firstDayOfWeek: FirstDayOfWeekValues) => {
    switch (firstDayOfWeek) {
        case 'MONDAY':
            return 1;
        case 'SUNDAY':
        default:
            return 0;
    }
};

export const getWeekOptions = (
    monthsBack: number,
    totalMonths: number,
    firstDayOfWeek: FirstDayOfWeekValues = 'SUNDAY',
): Option[] => {
    // This is on purpose implemeted to diregard the locale's definition of what's the first day of the week.
    // Hence the use of 'isoWeekday' instead of 'weekday'.
    // Reason beeing that we want the backend to control it (via behavior payload).
    const startOfRange = moment()
        .startOf('month')
        .subtract(monthsBack, 'months')
        .isoWeekday(firstDayOfWeekIsoValue(firstDayOfWeek));

    const endOfRange = startOfRange.clone().add(totalMonths, 'months').endOf('month');

    const options = new Array<Option>();

    let currentWeek = startOfRange.clone();

    while (currentWeek.isBefore(endOfRange)) {
        const endOfWeek = currentWeek.clone().add(6, 'days');

        options.push({
            label: `${currentWeek.format('LL')} - ${endOfWeek.format('LL')}`,
            value: createWeekValue(currentWeek, endOfWeek),
        });

        currentWeek = currentWeek.add(7, 'days');
    }

    return options;
};

export const parseWeekPeriodOption = (weekPeriodOptionValue: string) => {
    const [fromString, toString] = weekPeriodOptionValue.split('|');

    const fromMoment = moment(fromString);
    const toMoment = moment(toString);

    return {
        fromMoment,
        toMoment,
    };
};

export const getInvoiceRangeFromFormData = (data?: InvoicingFormData) => {
    if (data) {
        const {periodType, invoiceMonth, invoiceWeek, due} = data;

        if (periodType === Constants.FORM_PERIOD_TYPE_MONTH) {
            const fromMoment = moment(invoiceMonth, 'YYYY-MM');
            const from = fromMoment.isValid() ? fromMoment.format('YYYY-MM-DD') : undefined;
            const to = fromMoment.isValid() ? fromMoment.clone().endOf('month').format('YYYY-MM-DD') : undefined;

            return {
                from,
                to,
                due,
            };
        } else if (periodType === Constants.FORM_PERIOD_TYPE_WEEK) {
            const {fromMoment, toMoment} = parseWeekPeriodOption(invoiceWeek);

            return {
                from: fromMoment.format('YYYY-MM-DD'),
                to: toMoment.format('YYYY-MM-DD'),
                due,
            };
        } else {
            const fromMoment = moment(data.from, 'YYYY-MM-DD');
            const from = fromMoment.isValid() ? fromMoment.format('YYYY-MM-DD') : undefined;

            const toMoment = moment(data.to, 'YYYY-MM-DD');
            const to = toMoment.isValid() ? toMoment.format('YYYY-MM-DD') : undefined;

            return {
                from,
                to,
                due,
            };
        }
    } else {
        return {};
    }
};

const getInitialWeek = (date: string, firstDayOfWeek: FirstDayOfWeekValues) => {
    const startOfWeek = moment(date, 'YYYY-MM-DD').isoWeekday(firstDayOfWeekIsoValue(firstDayOfWeek));
    const endOfWeek = startOfWeek.clone().add(6, 'days');
    const initialWeek = createWeekValue(startOfWeek, endOfWeek);

    return initialWeek;
};

export const getInitialValues = (initialPeriod: Period, firstDayOfWeek?: FirstDayOfWeekValues) => {
    if (hasValue(initialPeriod?.from)) {
        const initialMonth = moment(initialPeriod.from, 'YYYY-MM-DD');
        const initialWeek = firstDayOfWeek ? getInitialWeek(initialPeriod.from, firstDayOfWeek) : undefined;

        return {
            invoiceMonth: initialMonth.format('YYYY-MM'),
            invoiceWeek: initialWeek,
            [Constants.FORM_PERIOD_TYPE]: Constants.FORM_PERIOD_TYPE_MONTH,
            from: initialPeriod.from,
            to: initialPeriod?.to,
        };
    } else {
        return {
            [Constants.FORM_PERIOD_TYPE]: Constants.FORM_PERIOD_TYPE_MONTH,
        };
    }
};
